import { handleActions } from 'redux-actions';
import ddt from '@wix/da-ddt';
import { DuperbrowseState, ActiveDuperbrowseStream } from './types';
import {
  initializeDuperbrowse,
  openDuperbrowse,
  exitDuperbrowse,
  setCurrentItem,
  shutdownDuperbrowse,
  exitDuperbrowseResorationTimeout,
  loadDuperbrowseItem,
  updateBiMetadata,
  setCurrentStreamByStackIndex,
} from './actionCreators';
import { reduxKey, initialState } from './constants';
import history from '../../redux/routing/history';
import { initDeviationPage } from '../../DeviationPage/redux/actions';

const logger = ddt.createLogger('duperbrowse');

const actionHandler = handleActions<DuperbrowseState, any>(
  {
    [initializeDuperbrowse.toString()]: (
      state,
      {
        payload: { initialStream, enableHistoryManagement },
      }: ReturnType<typeof initializeDuperbrowse>
    ) => ({
      ...state,
      rootStream: initialStream,
      lastViewedItem: undefined,
      isHistoryManagementEnabled: enableHistoryManagement ?? true,
    }),
    [openDuperbrowse.toString()]: (
      state,
      { payload }: ReturnType<typeof openDuperbrowse>
    ) => ({
      ...state,
      streamStack: [payload, ...state.streamStack],
      isSessionActive: true,
      rootLocation: state.isSessionActive
        ? state.rootLocation
        : { ...history.location },
      lastViewedItem: undefined,
    }),
    [exitDuperbrowse.toString()]: state => {
      const [topStream, ...restStack] = state.streamStack;
      return {
        ...state,
        streamStack: restStack,
        isSessionActive: restStack.length > 0,
        lastViewedItem: topStream
          ? {
              streamId: topStream.streamId,
              itemId: topStream.currentOpenItem,
            }
          : undefined,
      };
    },
    [exitDuperbrowseResorationTimeout.toString()]: state => {
      return {
        ...state,
        lastViewedItem: undefined,
      };
    },
    [shutdownDuperbrowse.toString()]: state => ({
      ...state,
      streamStack: [],
      isSessionActive: false,
      lastViewedItem:
        state.streamStack && state.streamStack.length
          ? {
              streamId:
                state.streamStack[state.streamStack.length - 1].streamId,
              itemId:
                state.streamStack[state.streamStack.length - 1].currentOpenItem,
            }
          : undefined,
    }),
    [setCurrentItem.toString()]: (
      state,
      { payload: { itemId } }: ReturnType<typeof setCurrentItem>
    ) => {
      const {
        streamStack: [topStream, ...restStack],
        rootStream,
      } = state;

      if (!topStream && !!rootStream) {
        return {
          ...state,
          rootStream: { ...rootStream, currentOpenItem: itemId },
        };
      }

      const stream: ActiveDuperbrowseStream = state.streamStack[0];
      if (!stream) {
        return state;
      }

      const updatedStream = { ...stream, currentOpenItem: itemId };

      return {
        ...state,
        streamStack: [updatedStream, ...restStack],
      };
    },
    [setCurrentStreamByStackIndex.toString()]: (
      state,
      {
        payload: { indexInStack, itemId },
      }: ReturnType<typeof setCurrentStreamByStackIndex>
    ) => {
      logger.log('reducer, setCurrentStreamByStackIndex', indexInStack, itemId);
      const stream = state.streamStack[indexInStack];
      logger.log(
        'reducer, setCurrentStreamByStackIndex, stream:',
        `index: ${indexInStack}, id: ${stream?.streamId}`
      );
      if (stream) {
        if (stream) {
          return {
            ...state,
            // pop all the other potential streams, and set to specified item
            streamStack: [
              { ...stream, currentOpenItem: itemId },
              ...state.streamStack.slice(indexInStack + 1),
            ],
          };
        }
      }

      // if not found, fall back to rootStream if possible
      if (state.rootStream) {
        logger.log(
          'reducer, setCurrentStreamByStackIndex, falling back to rootStream'
        );
        return {
          ...state,
          streamStack: [],
          isSessionActive: false,
          rootStream: {
            ...state.rootStream,
            currentOpenItem: itemId,
          },
        };
      }
      return state;
    },
    [loadDuperbrowseItem.toString()]: (
      state,
      { payload: { itemSchema } }: ReturnType<typeof loadDuperbrowseItem>
    ) => {
      if (itemSchema !== 'deviation') {
        return state;
      }
      return { ...state, loadCount: (state.loadCount ?? 0) + 1 };
    },
    [initDeviationPage.toString()]: (
      state,
      {
        payload: { biMetadata, deviation },
      }: ReturnType<typeof initDeviationPage>
    ) => {
      return {
        ...state,
        currentBiMetadata: {
          ...state.currentBiMetadata,
          [deviation.deviationId]: biMetadata,
        },
      };
    },
    [updateBiMetadata.toString()]: (
      state,
      {
        payload: { biMetadata, deviation },
      }: ReturnType<typeof updateBiMetadata>
    ) => {
      const current = state.currentBiMetadata?.[deviation.deviationId];
      if (current) {
        return {
          ...state,
          currentBiMetadata: {
            ...state.currentBiMetadata,
            [deviation.deviationId]: appendJSON(current, biMetadata),
          },
        };
      }

      return {
        ...state,
        currentBiMetadata: {
          ...state.currentBiMetadata,
          [deviation.deviationId]: biMetadata,
        },
      };
    },
  },
  initialState
);

/**
 * Avoids using json.parse as its much slower and we are not interested in the data itself
 */
function appendJSON(currentBiMetadata: string, newBiMetadata: string) {
  let newData;
  if (
    !newBiMetadata ||
    newBiMetadata === '' ||
    !newBiMetadata.startsWith('[')
  ) {
    return currentBiMetadata; // nothing to do here
  } else {
    // remove the [] wrapper from the incoming data, leaves use with {}
    newData = newBiMetadata.replace(/^\[/, '').replace(/\]$/, '');
  }

  if (
    !currentBiMetadata ||
    currentBiMetadata === '' ||
    !currentBiMetadata.startsWith('[')
  ) {
    return `[]`;
  }

  // Remove the last ] and append the new data to the end + ]
  return currentBiMetadata.replace(/\]$/, `,${newData}]`);
}

export default {
  [reduxKey as string]: actionHandler,
};
